/**
 * Aba Atualização: que versão estou usando e como trocar de versão.
 *
 * Morava dentro da Ajuda, e as duas coisas não se pareciam: uma é uma AÇÃO que
 * se faz de vez em quando, a outra é leitura para quando algo dá errado. Quem
 * queria atualizar tinha que passar por texto de suporte, e quem queria
 * entender um problema passava por um botão que não tinha nada a ver.
 *
 * Um componente só para os dois lugares — o site (navegador) e o app
 * (Electron) —, porque a pergunta é a mesma. O que muda é o que dá para
 * responder:
 *
 * - No APP, existe versão instalada e existe atualizador. O botão procura,
 *   baixa e instala.
 * - No SITE, não há nada instalado para atualizar. O botão vira o download do
 *   instalador, e a versão mostrada é a mais recente publicada — a do
 *   servidor, não a de ninguém.
 *
 * Prometer "atualizar" num navegador seria um botão que não faz o que diz.
 */
import { useCallback, useEffect, useState, type CSSProperties, type ReactElement } from "react";
import { compareVersions } from "@shared/protocol";
import type { UpdateState } from "@/media/lfrcordDesktop";
import { C, FONT_MONO } from "@/theme";

const ROTULO: CSSProperties = {
  fontFamily: FONT_MONO,
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: ".12em",
  color: C.textFaint,
  textTransform: "uppercase",
};

const CARTAO: CSSProperties = {
  padding: "14px 16px",
  border: `1px solid ${C.lineSoft}`,
  borderRadius: 12,
  background: C.field,
};

const TEXTO: CSSProperties = {
  fontSize: 13,
  lineHeight: 1.6,
  color: C.textSoft,
  margin: 0,
};

const BOTAO: CSSProperties = {
  padding: "9px 16px",
  border: 0,
  borderRadius: 9,
  background: C.accent,
  color: C.ink,
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
};

/** Resposta de `/api/desktop/latest`. */
type Publicada =
  | { available: true; version: string; url: string; size: number }
  | { available: false };

function Linha({ rotulo, valor }: { rotulo: string; valor: string }): ReactElement {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "5px 0" }}>
      <span style={{ ...ROTULO, letterSpacing: ".08em" }}>{rotulo}</span>
      <span style={{ fontFamily: FONT_MONO, fontSize: 12, color: C.textStrong }}>{valor}</span>
    </div>
  );
}

/**
 * O rótulo do botão diz a AÇÃO, sempre.
 *
 * Já tentei pôr o resultado aqui ("Você já está atualizado") e ficou pior: um
 * botão que anuncia um estado não parece clicável, e quem quisesse conferir de
 * novo não sabia que podia. O resultado tem lugar próprio — o recado logo
 * abaixo — e é lá que ele fica.
 */
function acaoDoEstado(estado: UpdateState): {
  texto: string;
  ocupado: boolean;
  emDia: boolean;
} {
  switch (estado.fase) {
    case "procurando":
      return { texto: "Procurando…", ocupado: true, emDia: false };
    case "baixando":
      return { texto: `Baixando… ${estado.porcento}%`, ocupado: true, emDia: false };
    case "pronta":
      return { texto: "Instalar e reiniciar", ocupado: false, emDia: false };
    case "atualizado":
      return { texto: "Checar atualização", ocupado: false, emDia: true };
    default:
      return { texto: "Checar atualização", ocupado: false, emDia: false };
  }
}

function recadoDoEstado(estado: UpdateState): { texto: string; cor: string } | null {
  switch (estado.fase) {
    case "atualizado":
      return {
        texto: "✓ Você já está na versão mais recente. Procurei agora mesmo.",
        cor: C.textSoft,
      };
    case "baixando":
      return { texto: `Baixando a versão ${estado.versao}…`, cor: C.textSoft };
    case "pronta":
      return {
        texto: `A versão ${estado.versao} está pronta. O app fecha e abre de novo já atualizado.`,
        cor: C.textStrong,
      };
    case "erro":
      return { texto: `Não consegui atualizar: ${estado.mensagem}`, cor: C.red };
    default:
      return null;
  }
}

export function Atualizacao(): ReactElement {
  const desktop = window.lfrcordDesktop;
  const [estado, setEstado] = useState<UpdateState>({ fase: "ocioso" });
  const [publicada, setPublicada] = useState<Publicada | null>(null);

  // O estado corrente chega na assinatura: quem abre esta aba depois de a
  // procura automática já ter rodado precisa ver o resultado dela.
  useEffect(() => {
    if (!desktop) return;
    return desktop.updates.onState(setEstado);
  }, [desktop]);

  // A versão publicada vale nos dois: no site é a que se baixa, e no app serve
  // para comparar com a instalada.
  useEffect(() => {
    let vivo = true;
    fetch("/api/desktop/latest")
      .then(r => (r.ok ? (r.json() as Promise<Publicada>) : null))
      .then(dados => {
        if (vivo && dados) setPublicada(dados);
      })
      .catch(() => {
        /* sem rede: a tela simplesmente não mostra a linha */
      });
    return () => {
      vivo = false;
    };
  }, []);

  const clicar = useCallback(() => {
    if (!desktop) return;
    if (estado.fase === "pronta") desktop.updates.install();
    else desktop.updates.check();
  }, [desktop, estado.fase]);

  const acao = acaoDoEstado(estado);
  const recado = recadoDoEstado(estado);
  const versaoPublicada = publicada?.available ? publicada.version : null;
  // Comparacao NUMERICA: por texto, "1.2.10" parece mais velha que "1.2.9".
  const atrasada =
    desktop != null &&
    versaoPublicada != null &&
    desktop.version.length > 0 &&
    compareVersions(desktop.version, versaoPublicada) < 0;

  return (
    <div style={{ maxWidth: 560, display: "grid", gap: 18 }}>
      <section style={CARTAO}>
        <div style={{ ...ROTULO, marginBottom: 8 }}>Versão</div>
        {desktop ? (
          <Linha rotulo="Instalada" valor={desktop.version || "desconhecida"} />
        ) : (
          <Linha rotulo="Onde você está" valor="Navegador" />
        )}
        {versaoPublicada ? <Linha rotulo="Mais recente" valor={versaoPublicada} /> : null}
        {desktop && versaoPublicada ? (
          <p
            style={{
              ...TEXTO,
              marginTop: 8,
              color: atrasada ? C.textStrong : C.textSoft,
            }}
          >
            {atrasada
              ? `Há uma versão mais nova publicada (${versaoPublicada}). Use o botão abaixo.`
              : "✓ Você está na versão mais recente publicada."}
          </p>
        ) : null}
      </section>

      <section style={CARTAO}>
        <div style={{ ...ROTULO, marginBottom: 10 }}>Atualização</div>

        {desktop ? (
          <>
            <p style={{ ...TEXTO, marginBottom: 12 }}>
              O LFRCORD procura atualização sozinho toda vez que abre, e se achar já instala.
              Este botão procura agora, sem esperar a próxima abertura.
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <button
                type="button"
                onClick={clicar}
                disabled={acao.ocupado}
                style={{
                  ...BOTAO,
                  // Em dia não é ação pendente: o botão sai do acento e vira
                  // um estado, mas continua clicável para procurar de novo.
                  background: acao.emDia ? C.field : C.accent,
                  color: acao.emDia ? C.textStrong : C.ink,
                  border: acao.emDia ? `1px solid ${C.lineStrong}` : 0,
                  opacity: acao.ocupado ? 0.65 : 1,
                  cursor: acao.ocupado ? "default" : "pointer",
                }}
              >
                {acao.texto}
              </button>
            </div>
          </>
        ) : (
          <>
            <p style={{ ...TEXTO, marginBottom: 12 }}>
              Você está pelo navegador, então não há nada instalado para atualizar. O app de
              computador atualiza sozinho ao abrir — e é ele que tem transmissão de tela em
              1080p60 e captura do áudio do sistema.
            </p>
            {publicada?.available ? (
              <a
                href={publicada.url}
                style={{ ...BOTAO, display: "inline-block", textDecoration: "none" }}
              >
                Baixar o app ({publicada.version})
              </a>
            ) : (
              <p style={{ ...TEXTO, color: C.textDim }}>
                O instalador ainda não foi publicado neste servidor.
              </p>
            )}
          </>
        )}

        {recado ? (
          <p style={{ ...TEXTO, marginTop: 12, color: recado.cor }}>{recado.texto}</p>
        ) : null}
      </section>

    </div>
  );
}
